import { useEffect,  useState } from 'react'
import { ApolloCache, FetchResult, useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { Modal, ModalHeader, ModalBody, ModalFooter, Label } from 'reactstrap';
import { ICategory, IChildOrgsResponse, IOrg, IRootOrgsResponse, isCategory } from '../../types/org';
import { LOCALES } from '../../i18n/locales';
import { GET_CATEGORIES, GET_CHILD_ORGS, GET_ORG_DETAILS, GET_ORG_RELATIONSHIPS, GET_ROOT_ORGS, IS_CHILD, MOVE_ORG } from '../../graphql/org-gql';
import useOrgTypes from '../../hook/useOrgTypes';
import { messages } from '../../i18n/message';
import AuthWrapper from '../common/auth-wrapper';
import { CancelIcon, MoveIcon } from '../common/icons';
import { UserRoleType } from '../../types/constants';
import OrgTree from '../common/org-tree/org-tree';
import MessageModal from '../common/message-modal';
import SpinnerOverlay from '../common/spinner/spinner';
import { notifyError, notifySuccess } from '../../services/notification';
import Select from '../common/select/select';
import { Action } from '../../types/individual';
import confirm from "../common/confirm";
import Button from '../common/button';

type TopOrgMoveProps = {
    category: ICategory,
    topOrgIds: number[],
    isOpen: boolean,
    toggle: () => void
}

const TopOrgMoveModal = (props: TopOrgMoveProps) => {
    const { category, topOrgIds, isOpen, toggle } = props
    const [movetoOrg, setMovetoOrg] = useState<ICategory | IOrg>();
    const [orgTypeId,setOrgTypeId] = useState<string>('');
    const [msg,setMsg] =  useState<string>('');
    const [messageModal, setMessageModal] = useState(false);
    const locale = LOCALES.ENGLISH;
    const [getOrgDetails,{loading:getOrgDetailsLoading}] = useLazyQuery(GET_ORG_DETAILS)
    const [moveOrgs,{loading:moveOrgsLoading}] = useMutation(MOVE_ORG,{
        update(cache) {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'orgPath' });
            cache.gc();
          }
    })
    const client = useApolloClient()
    const { categories } = client.readQuery({query: GET_CATEGORIES})
    const { orgTypes } = useOrgTypes(movetoOrg)
    const [isChild,{loading:isChildLoading,data:isMovetoChild}]=useLazyQuery(IS_CHILD)
    const [getRootOrgs, {loading: rootLoading}] = useLazyQuery<IRootOrgsResponse>(GET_ROOT_ORGS,{fetchPolicy: 'network-only'})
    const [getChildOrgs, {loading: childLoading}] = useLazyQuery<IChildOrgsResponse>(GET_CHILD_ORGS,{fetchPolicy: 'network-only'})
    useEffect(() => {
        
        if(!movetoOrg) return;
        if(isCategory(movetoOrg) && movetoOrg.type !== 'ORGANIZATION')
            setMsg(messages[locale].ERROR['5048'])
        else if(movetoOrg.id === category.id)
            setMsg(messages[locale].ERROR['5047'])
        else if(topOrgIds.includes(movetoOrg?.id||0))
            setMsg(messages[locale].ERROR['5046'])
        return ()=>{setMsg('');}
    }, [movetoOrg]);

    useEffect(()=>{
        if(!isCategory(movetoOrg) && orgTypes && movetoOrg?.parentOrg!==null && movetoOrg?.details?.category.categoryName==='Ministries, Secretariats and Offices'){
            const targetOrgType = movetoOrg.details.orgType
            const index = orgTypes.findIndex(item=>item.id===targetOrgType.id)
            
            if(orgTypes.length>index+1 ){
                setOrgTypeId(orgTypes[index+1]?.id?.toString())
            }
        }else{
            setOrgTypeId('');
        }

    },[orgTypes,movetoOrg])

    useEffect(() => {
        isMovetoChild?.isChild && setMsg(messages[locale].ERROR['5049']);
        return ()=>{setMsg('');}
    },[isMovetoChild,locale]);

    useEffect(()=>{
        msg && setMessageModal(true)
    },[msg])

    const onSelectMovetoOrg = async (movetoOrg?: ICategory | IOrg) => {
        if(movetoOrg){
            setMsg('');
            
            if(!isCategory(movetoOrg)){
                const orgResponse = await getOrgDetails({variables:{id:movetoOrg?.id}})
                orgResponse.data.org && setMovetoOrg(orgResponse.data.org);
                (category.id !==movetoOrg.id  && !topOrgIds.includes(movetoOrg.id))
                    && isChild({variables:{parentIds:topOrgIds,childId:movetoOrg.id}})
            }else{
                setMovetoOrg(movetoOrg)
            }

        }
    }

    const onMoveOrgs = () => {

        moveOrgs({
            variables: {
                orgId: movetoOrg?.id,
                action: Action.MOVE_RELATIONSHIP_VALIDATION,
                input: {orgId: movetoOrg?.id,childOrgIds: topOrgIds,relTypeId: orgTypeId,parentCategory: isCategory(movetoOrg)}
            },
            onCompleted: (data) => {
                //console.log("data for validation:",data)
                if(data?.moveOrgs.success){
                    moveOrgs({
                        variables: {
                            orgId: movetoOrg?.id,
                            action: Action.MOVE_RELATIONSHIP,
                            input: {orgId: movetoOrg?.id,childOrgIds: topOrgIds,relTypeId: orgTypeId,parentCategory: isCategory(movetoOrg)}
                        },
                        refetchQueries: getRefetchQuery(),
                        awaitRefetchQueries: true,
                        update(cache,{data}){updateCache(cache,data,topOrgIds,category,movetoOrg)},
                        onCompleted: (data) => {
                            //console.log("data for execution:",data)
                            toggle()
                            data && notifySuccess()
                        }
                    })
                }else if(data?.moveOrgs.message){
                    notifyError(data.moveOrgs.message)
                }
            }
        })     
        setOrgTypeId('');
        setMovetoOrg(undefined);
    }

    const updateCache = (cache : ApolloCache <any>, result: any,topOrgIds:number[],category:ICategory,movetoOrg:ICategory|IOrg|undefined) => {
        
        //remove orgPath
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'orgPath' });
        cache.gc();
       
        // update hasChild for toOrg
        cache.modify({
            id: `Org:${movetoOrg?.id}`,
            fields: {
                hasChild() {
                    return true;
                }
            }
        })

        //update category topOrgs
        const cachedTopOrgs = cache.readQuery<{rootOrgs:IOrg[]}>({
            query:GET_ROOT_ORGS,
            variables:{categoryId:category.id}
        })?.rootOrgs;
        const updatedTopOrgs = cachedTopOrgs?.filter(topOrg=>!topOrgIds.includes(topOrg.id))      
        cache.writeQuery({
            query:GET_ROOT_ORGS,
            variables:{categoryId:category.id},
            data:{
                rootOrgs:updatedTopOrgs
            }
        })

        //update to org
        if(isCategory(movetoOrg)){
            getRootOrgs({variables:{ categoryId: movetoOrg.id}})

        }else{
            //update to Org children
            getChildOrgs({variables:{id:movetoOrg?.id}})
        } 
    }

    const getRefetchQuery = () => {
        return [{query: GET_ORG_RELATIONSHIPS, variables: { orgId: movetoOrg?.id }}]

    }

    const changeOrgType = (event: any) => {
        setOrgTypeId(event.target.value);
    }

    const onCancel=()=>{
        setMovetoOrg(undefined);
        setOrgTypeId('');
        toggle();
    }
    const messageToggle = ()=>{
        setMessageModal(!messageModal)
        //setMsg('');
    }

    return (
    <>
        {(moveOrgsLoading || isChildLoading || getOrgDetailsLoading ||rootLoading||childLoading ) && <SpinnerOverlay/>}
        {messageModal && <MessageModal isOpen={messageModal} titleText='Warning!' toggle={messageToggle}>{msg}</MessageModal>}
        {<Modal size="lg" isOpen={isOpen} toggle={onCancel} backdrop="static" scrollable={true}>
            <ModalHeader toggle={onCancel}>Move Selected Related Organizations</ModalHeader>
            <ModalBody>
                <div>Please select which organization you would like to move the selected related organization to.</div>
                <OrgTree roots={categories || []} isGlobal={false} onSelected={onSelectMovetoOrg}></OrgTree>

                <div className='row'>
                <div className='col-md-4'><Label for="orgType" aria-label='orgType'>Move as Type:</Label></div>
                    <div className='col-md-8'>
                        <Select id="orgType" aria-label='orgType'   value={orgTypeId || ''} disabled={movetoOrg!=undefined?false:true}
                            onChange={changeOrgType} style={{width:'100%'}} required>
                            <option key={0} value="" disabled hidden aria-label='select'>Select a type to move as...</option>
                            {orgTypes?.map(item =>
                                <option key={item.id} value={item.id} aria-label={item.label}>
                                    {item.label}
                                </option>)}
                        </Select>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <AuthWrapper role={[UserRoleType.ADMINISTRATOR,UserRoleType.COORDINATOR]}>
                    {(authorized:boolean)=>
                        <Button color="primary" disabled={(movetoOrg&&orgTypeId&&!msg&&authorized)?false:true} onClick={onMoveOrgs} >
                            <MoveIcon/>
                            Move
                        </Button>
                    }
                </AuthWrapper>

                <Button color="secondary" onClick={onCancel}>
                    <CancelIcon/>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>}
    </>
    )
}

export default TopOrgMoveModal;
